import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Link as MuiLink,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';

import { authApi } from '../../services/authApi';
import { ApiError } from '../../services/api';

/** Mirrors the API rule (10–200 chars) plus a client-side confirm field. */
const ResetSchema = z
  .object({
    password: z
      .string()
      .min(10, 'Password must be at least 10 characters long.')
      .max(200, 'Password is too long.'),
    confirmPassword: z.string().min(1, 'Please re-enter your password.'),
  })
  .refine((v) => v.password === v.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords don’t match.',
  });

type ResetFields = z.infer<typeof ResetSchema>;

/**
 * Set-a-new-password page (#122). Reads the single-use token from the URL
 * (`/reset-password?token=…`), posts it with the new password, and on success
 * sends the user to sign in. A missing token short-circuits to an error.
 */
export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetFields>({
    resolver: zodResolver(ResetSchema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  async function onSubmit(values: ResetFields) {
    setSubmitting(true);
    setError(null);
    try {
      await authApi.resetPassword(token, values.password);
      setDone(true);
      // Brief confirmation, then off to sign in.
      setTimeout(() => navigate('/login', { replace: true }), 1500);
    } catch (err) {
      if (err instanceof ApiError && err.httpStatus === 400 && err.serverMessage) {
        setError(err.serverMessage);
      } else if (err instanceof ApiError && err.httpStatus === 429) {
        setError('Too many attempts. Please wait a minute and try again.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box sx={{ maxWidth: 480, mx: 'auto' }}>
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 4 } }}>
        <Typography variant="h1" component="h1" sx={{ fontSize: '1.6rem', mb: 1 }}>
          Choose a new password
        </Typography>

        {done ? (
          <Alert severity="success" role="status">
            Your password has been reset. Redirecting you to sign in…
          </Alert>
        ) : !token ? (
          <Alert severity="error" role="alert">
            This reset link is missing its token. Please{' '}
            <MuiLink component={RouterLink} to="/forgot-password">
              request a new link
            </MuiLink>
            .
          </Alert>
        ) : (
          <>
            {error ? (
              <Alert severity="error" role="alert" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : null}

            <Box
              component="form"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              aria-label="Choose a new password"
            >
              <Stack spacing={2}>
                <TextField
                  label="New password"
                  type="password"
                  autoComplete="new-password"
                  required
                  fullWidth
                  {...register('password')}
                  error={Boolean(errors.password)}
                  helperText={errors.password?.message ?? 'At least 10 characters.'}
                  inputProps={{ 'aria-invalid': Boolean(errors.password) }}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Confirm new password"
                  type="password"
                  autoComplete="new-password"
                  required
                  fullWidth
                  {...register('confirmPassword')}
                  error={Boolean(errors.confirmPassword)}
                  helperText={errors.confirmPassword?.message ?? ' '}
                  inputProps={{ 'aria-invalid': Boolean(errors.confirmPassword) }}
                  InputLabelProps={{ shrink: true }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={submitting}
                  aria-busy={submitting}
                >
                  {submitting ? 'Resetting…' : 'Reset password'}
                </Button>
              </Stack>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
}

export default ResetPasswordPage;
